import { PSP } from "./key-management/key-dto";

export enum WelcomePackStatus {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'IN-ACTIVE'
}

export enum WelcomePackStepKey {
    BASE_RELEASE = 'base_release',
    KEY_DISTRIBUTION = 'key_distribution',
    AFC_UPDATE = 'afc_update',
    FAILED_PROCESSING = 'failed_processing'
}
export enum WelcomePackStepType {
    SOFTWARE_UPDATE = "SOFTWARE-UPDATE",
    KEY_DISTRIBUTION = "KEY-DISTRIBUTION",
    FAILURE = "FAILURE"
}
interface IStepType {
    key: string; // 'base_release' | 'key_distribution' | 'afc_update' | 'failed_processing'
    type: WelcomePackStepType;
    groupName: string;
    name?: string;
}
export interface IReleaseStep extends IStepType {
    releaseId?: number;
    jobId?: string;
}

export interface IKeyDistributionStep extends IStepType {
    pspKeys?: PSP[];
}

export interface ICreateWelcomePackOptions {
    label: string;
    stepConfigurations: IWPStepConfiguration[];
}

export interface IWPStepConfiguration {
    key: string; // 'base_release' | 'key_distribution' | 'afc_update' | 'failed_processing'
    type: WelcomePackStepType;
    releaseId?: number;
    pspKeys?: PSP[];
}

export class WelcomePackDto {
    constructor(private readonly _object: unknown) { }

    get id(): number {
        return this._object['id'];
    }

    get label(): string {
        return this._object['label'] || '';
    }

    get status(): WelcomePackStatus {
        return this._object['status'] || '';
    }

    get version(): number {
        return this._object['version'] || 1;
    }

    get accountUuid(): string {
        return this._object['accountUuid'] || '';
    }

    private get stepDefinition(): any {
        return this._object['stepDefinition'] || {};
    }

    get baseRelease(): IReleaseStep {
        const stepConfiguration = this.stepDefinition['steps']?.[WelcomePackStepKey.BASE_RELEASE];
        if (!stepConfiguration) {
            return null;
        }
        return {
            key: stepConfiguration.key,
            name: 'Base Release',
            groupName: stepConfiguration.groupName,
            type: stepConfiguration.type,
            jobId: stepConfiguration.data?.jobId,
            releaseId: stepConfiguration.data?.releaseId,
        }
    }

    get keyDistribution(): IKeyDistributionStep {
        const stepConfiguration = this.stepDefinition['steps']?.[WelcomePackStepKey.KEY_DISTRIBUTION];
        if (!stepConfiguration) {
            return null;
        }
        return {
            key: stepConfiguration.key,
            name: 'Key Distribution',
            groupName: stepConfiguration.groupName,
            type: stepConfiguration.type,
            pspKeys: stepConfiguration.data?.pspKeys
        };
    }

    get afcUpdate(): IReleaseStep {
        const stepConfiguration = this.stepDefinition['steps']?.[WelcomePackStepKey.AFC_UPDATE];
        if (!stepConfiguration) {
            return null;
        }
        return {
            key: stepConfiguration.key,
            name: 'AFC Update',
            groupName: stepConfiguration.groupName,
            type: stepConfiguration.type,
            jobId: stepConfiguration.data?.jobId,
            releaseId: stepConfiguration.data?.releaseId,
        };
    }

    get failedProcessing(): IReleaseStep {
        const stepConfiguration = this.stepDefinition['steps']?.[WelcomePackStepKey.FAILED_PROCESSING];
        if (!stepConfiguration) {
            return null;
        }
        return {
            key: stepConfiguration.key,
            name: 'Failed Processing',
            groupName: stepConfiguration.groupName,
            type: stepConfiguration.type,
        };
    }

    get allStepConfigurations(): IReleaseStep[] {
        return [
            this.baseRelease,
            this.keyDistribution,
            this.afcUpdate,
            this.failedProcessing
        ].filter(s => !!s);
    }

    get createdAt(): string {
        return this._object['createdAt'] || '';
    }

    get updatedAt(): string {
        return this._object['updatedAt'] || '';
    }

    get createdAtDate(): Date {
        return new Date(this.createdAt);
    }

    get updatedAtDate(): Date {
        return new Date(this.updatedAt);
    }

    toJSON() {
        return Object.assign({}, this._object);
    }
}

export class DeviceExecutionDto {
    constructor(private readonly _object: unknown = {}) { }

    get id(): number {
        return this._object['id'];
    }

    get UID(): string {
        return this._object['deviceName'] || '';
    }

    get deviceLabel(): string {
        return this._object['deviceLabel'] || '';
    }

    get type(): string {
        return this._object['deviceTypeName'] || '';
    }

    get serialNumber(): string {
        return this._object['serialNumber'] || '';
    }

    get esn(): string {
        return this._object['esn'] || '';
    }

    get location(): string {
        return this._object['location'] || '';
    }

    get firmwareVersion(): string {
        return this._object['firmwareVersion'] || '';
    }

    get trampolineVersion(): string {
        return this._object['trampolineVersion'] || '';
    }

    get lastUpdated(): number {
        return this._object['lastUpdated'] || null;
    }

    get jobExecutionId(): string {
        return this._object['jobExecutionDetails']?.jobId || '';
    }

    get jobExecutionStatus(): string {
        return this._object['jobExecutionDetails']?.status || '';
    }

    get jobExecutionFailureStep(): string {
        return this._object['jobExecutionDetails']?.failureStep || 'UNKNOWN';
    }

    get jobExecutionFailureReason(): string {
        return this._object['jobExecutionDetails']?.failureReason || 'UNKNOWN';
    }
}